import { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion, useReducedMotion } from 'framer-motion';
import { CheckIcon, GiftIcon } from 'lucide-react';
import { ProductImage } from '../../ui/ProductImage';
import { useCartActions } from '../../contexts/StoreContext';
import { founder } from '../../data/content';
import { shopLabels } from '../../data/shopLabels';
import { countSoldOut, isAllSoldOut, isSoldOut } from '../../core/rules/stock';
import { treatmentDuration, treatmentPrice } from '../../utils/catalog';
import type { GiftTier } from '../../data/gifts';
import type { Product, Routine, Treatment } from '../../core/types/catalog';
import { euro } from '../../core/format/currency';
import { ease } from '../../core/format/motion';
import { QuizEmailForm } from './QuizEmailForm';
import { track } from '../../utils/analytics';
type Props = {
  result: Routine;
  totals: {
    items: Product[];
    price: number;
    months: string;
  };
  gift: GiftTier | null | undefined;
  starter: Product | undefined;
  /** De behandeling in de salon die bij dit advies hoort (kan ontbreken). */
  treatment: Treatment | undefined;
  answers: readonly string[];
  /** Nieuwe sleutel na "Opnieuw doen", zodat het formulier leeg begint. */
  round: number;
  onClose: () => void;
  onCloseKeep: () => void;
  onRestart: () => void;
  onBookTreatment: (slug: string) => void;
};

/**
 * De uitslag. Volgorde = de beslissing: wat je krijgt (protocol en producten), wat erbij zit,
 * dan de andere routes: klein beginnen, eerst in de salon, of rustig thuis nalezen per mail.
 * De hoofdknop (alles in de mand) staat vast onderaan de lade (QuizResultFooter), altijd in beeld.
 */
export function QuizResult({
  result,
  totals,
  gift,
  starter,
  treatment,
  answers,
  round,
  onClose,
  onCloseKeep,
  onRestart,
  onBookTreatment
}: Props) {
  const reduce = useReducedMotion();
  const {
    add
  } = useCartActions();
  const headingRef = useRef<HTMLHeadingElement>(null);
  const soldOutCount = countSoldOut(totals.items);
  const allSoldOut = isAllSoldOut(totals.items);

  // De laatste antwoordknop is weg: focus naar de uitslag.
  useEffect(() => headingRef.current?.focus(), []);
  // Eén keer per uitslag (ook na "Opnieuw doen"): welk protocol, nooit de antwoorden.
  useEffect(() => track('huidadvies-afgerond', {
    routine: result.id
  }), [result.id, round]);
  return <motion.div initial={reduce ? false : {
    opacity: 0,
    y: 10
  }} animate={{
    opacity: 1,
    y: 0
  }} transition={{
    duration: 0.26,
    ease
  }} data-id="element-8214" data-mp="105akg2w6q1du">
      <div className="flex items-center gap-3" data-id="element-8215" data-mp="1dza8t8pln0ne">
        {/* Decoratief: haar naam staat ernaast. */}
        <img src={founder.portrait} alt="" width={44} height={44} className="h-11 w-11 rounded-full object-cover" data-id="element-8216" data-mp="190qh0robqun4" />
        <p className="text-[14px] leading-snug text-muted" data-id="element-8217" data-mp="1iwtlw5ak97dr">
          <span className="block text-ink" data-id="element-8218" data-mp="1ixbn7gc2ybsj">Mijn advies voor jou</span>
          {founder.name}, huidspecialist
        </p>
      </div>
      <h3 ref={headingRef} tabIndex={-1} className="mt-6 font-display text-[2rem] font-light leading-[1.1] text-ink focus:outline-none" data-id="element-8219" data-mp="3ko6nolh8ez5l">
        <span className="sr-only" data-id="element-8220" data-mp="206xknbrm0lsh">Jouw advies: </span>
        {result.name}
      </h3>

      <ul className="mt-4 flex flex-wrap gap-1.5" aria-label="Op basis van wat je aangaf" data-id="element-8221" data-mp="2558s5fufmk7j">
        {answers.map((a, i) => <li key={`${i}-${a}`} className="rounded-full bg-sand px-3 py-1 text-[12.5px] text-ink" data-id="element-8222" data-mp="1qfjmb7be9s0x" data-mpi={i}>
            {a}
          </li>)}
      </ul>

      <p className="mt-5 text-[15px] leading-relaxed text-muted" data-id="element-8223" data-mp="1hfmrsp2bpyxy">{result.story}</p>

      <ul className="mt-6 divide-y divide-line border-y border-line" aria-label={`Producten in ${result.name}`} data-id="element-8224" data-mp="1lgzz73l2ea6z">
        {totals.items.map(p => <li key={p.id} className="flex items-center gap-3 py-3" data-id="element-8225" data-mp="17iiezfudbftt">
            <ProductImage src={p.image} alt="" width={48} className="h-14 w-12 shrink-0 p-1" data-id="element-8226" data-mp="1cfs0wlijt9cw" />
            <div className="min-w-0 flex-1" data-id="element-8227" data-mp="31vhsn3f6tzyg">
              <p className="text-[12px] text-clay" data-id="element-8228" data-mp="31vrgolgo9x1i">{isSoldOut(p) ? shopLabels.soldOutShort : p.step}</p>
              <p className="text-[15px] leading-snug text-ink" data-id="element-8229" data-mp="36tjl2eyi7bml">{p.name}</p>
            </div>
            <span className={`text-[14px] tabular-nums text-muted ${isSoldOut(p) ? 'line-through decoration-line' : ''}`} data-id="element-8230" data-mp="16rjqjkyzlxv3">{euro(p.price)}</span>
          </li>)}
      </ul>

      <ul className="mt-4 space-y-1.5" aria-label="Inbegrepen" data-id="element-8231" data-mp="xqc543hkkrif">
        {result.included.map(inc => <li key={inc} className="flex items-start gap-2 text-[13px] leading-snug text-muted" data-id="element-8232" data-mp="srinpzz5wgcz">
            <CheckIcon className="mt-0.5 h-3.5 w-3.5 shrink-0 text-sage" strokeWidth={2} aria-hidden="true" data-id="element-8233" data-mp="ntzbf5m0ydno" />
            {inc}
          </li>)}
      </ul>

      {gift && <p className="mt-5 flex items-start gap-2.5 rounded-2xl bg-blush p-4 text-[13px] leading-relaxed text-ink" data-id="element-8234" data-mp="e2mkcg81vrip">
          <GiftIcon className="mt-0.5 h-4 w-4 shrink-0 text-ink" strokeWidth={1.5} aria-hidden="true" data-id="element-8235" data-mp="a33y9h4htaea" />
          <span data-id="element-8236" data-mp="51enm2l6myqq">
            Met dit protocol ligt <span className="font-medium" data-id="element-8237" data-mp="54karca93qjt">{gift.title}</span> voor je klaar: {gift.description}
          </span>
        </p>}

      {soldOutCount > 0 && !allSoldOut && <p className="mt-3 text-[13px] leading-relaxed text-muted" data-id="element-8238" data-mp="2bqgk2kkx9e2u">{shopLabels.partlySoldOut(soldOutCount)}</p>}

      {/* De andere routes, rustig onder elkaar. */}
      <div className="mt-8 divide-y divide-line border-t border-line" data-id="element-8239" data-mp="2lomjmvj9a84j">
        {starter && !allSoldOut && <button type="button" onClick={() => {
        add(starter, {
          via: 'huidadvies'
        });
        onClose();
      }} className="group flex w-full items-center justify-between gap-4 py-4 text-left" data-id="element-8240" data-mp="2ppq1fnkv6e8e">
            <span data-id="element-8241" data-mp="2216qt1ecdpd5">
              <span className="block text-[15px] text-ink" data-id="element-8242" data-mp="220x1zh519o91">Liever klein beginnen?</span>
              <span className="mt-0.5 block text-[13px] leading-snug text-muted" data-id="element-8243" data-mp="26rna324ug57f">
                Alleen {starter.name} · {euro(starter.price)}
              </span>
            </span>
            <span className="shrink-0 text-[14px] text-ink underline decoration-ink/25 underline-offset-4 group-hover:decoration-ink" data-id="element-8244" data-mp="1ebkvwq505way">In de mand</span>
          </button>}
        {treatment && <button type="button" onClick={() => onBookTreatment(treatment.slug)} aria-haspopup="dialog" className="group flex w-full items-center justify-between gap-4 py-4 text-left" data-id="element-8245" data-mp="2cnf4hbib641w">
            <span data-id="element-8246" data-mp="2re4dq8c7l934">
              <span className="block text-[15px] text-ink" data-id="element-8247" data-mp="2mdgrdzyxdgf8">Liever eerst in de salon?</span>
              <span className="mt-0.5 block text-[13px] leading-snug text-muted" data-id="element-8248" data-mp="1ynmghny621xm">
                {treatment.name} · {treatmentDuration(treatment)} · {treatmentPrice(treatment)}
              </span>
            </span>
            <span className="shrink-0 text-[14px] text-ink underline decoration-ink/25 underline-offset-4 group-hover:decoration-ink" data-id="element-8249" data-mp="27ou1537rf498">Plannen</span>
          </button>}
      </div>

      <QuizEmailForm key={round} routineId={result.id} answers={answers} data-id="element-8250" data-mp="3j37jjsjqurhv" />

      <div className="mt-6 flex items-center justify-between text-[13px]" data-id="element-8251" data-mp="3j5bxz4f9r8we">
        <button type="button" onClick={onRestart} className="-mx-2 px-2 py-2 text-muted transition-colors duration-150 ease-soft hover:text-ink" data-id="element-8252" data-mp="3f1ci8o1tce43">
          Opnieuw doen
        </button>
        <Link to="/routines" onClick={onCloseKeep} className="-mx-2 px-2 py-2 text-ink underline-offset-4 hover:underline" data-id="element-8253" data-mp="3rl9hgzmtanls">
          Alle protocollen bekijken
        </Link>
      </div>
    </motion.div>;
}